import React, { useEffect, useRef, useState } from 'react';
import { 
  FaStar, 
  FaQuoteLeft, 
  FaArrowLeft, 
  FaArrowRight,
  FaGoogle,
  FaFacebook,
  FaWhatsapp
} from 'react-icons/fa';

const Testimonials = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) observer.observe(headerRef.current);

    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      id: 1,
      name: 'Rahul Sharma',
      location: 'Varanasi',
      rating: 5,
      review: 'Excellent service! My bike is running like new after the servicing. The technicians are very professional and explained everything clearly. Highly recommended!',
      image: 'https://randomuser.me/api/portraits/men/32.jpg',
      vehicle: 'Royal Enfield Classic 350',
      date: '2 days ago'
    },
    {
      id: 2,
      name: 'Priya Singh',
      location: 'Sarnath',
      rating: 5,
      review: 'Best car service center in Varanasi. They did a complete checkup of my car and fixed all issues at reasonable prices. Very transparent and honest service.',
      image: 'https://randomuser.me/api/portraits/women/68.jpg',
      vehicle: 'Maruti Suzuki Swift',
      date: '1 week ago'
    },
    {
      id: 3,
      name: 'Amit Kumar',
      location: 'Sigra',
      rating: 4,
      review: 'Quick service and genuine parts. My car AC is working perfectly now. The staff is friendly and the prices are affordable. Will visit again.',
      image: 'https://randomuser.me/api/portraits/men/45.jpg',
      vehicle: 'Hyundai i20',
      date: '3 days ago'
    },
    {
      id: 4,
      name: 'Neha Verma',
      location: 'Lanka',
      rating: 5,
      review: 'Amazing experience! They picked up my bike from home and delivered after service. The quality of work is outstanding. Thank you Ambris Service!',
      image: 'https://randomuser.me/api/portraits/women/89.jpg',
      vehicle: 'Honda Activa',
      date: '5 days ago'
    },
    {
      id: 5,
      name: 'Vikash Patel',
      location: 'Bhelupur',
      rating: 5,
      review: 'Very satisfied with their service. They diagnosed the engine problem quickly and fixed it same day. Reasonable pricing and friendly staff. Best service center in town!',
      image: 'https://randomuser.me/api/portraits/men/56.jpg',
      vehicle: 'Tata Nexon',
      date: '1 day ago'
    }
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FaStar key={i} className={`text-xs ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`} />
    ));
  };

  return (
    <section ref={sectionRef} className="py-4 md:py-10g-gradient-to-br from-gray-50 to-red-50">
      <div className="container mx-auto px-4 md:px-4 max-w-7xl">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-12 opacity-0">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            What Our <span className="text-red-600">Customers Say</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Real stories from our satisfied customers who trust us with their vehicles
          </p>
          <div className="w-20 h-0.5 bg-red-500 mx-auto mt-6"></div>
        </div>

        {/* Desktop Testimonials Grid - 5 Cards */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-5 gap-5">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="bg-white rounded-2xl p-5 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <FaQuoteLeft className="text-red-200 text-2xl mb-3" />
              <p className="text-gray-600 text-xs leading-relaxed mb-3 line-clamp-4">
                "{testimonial.review}"
              </p>
              <div className="flex gap-1 mb-2">{renderStars(testimonial.rating)}</div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-semibold text-gray-800 text-xs">{testimonial.name}</h4>
                  <p className="text-gray-400 text-[10px]">{testimonial.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="w-full flex-shrink-0 px-2">
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <FaQuoteLeft className="text-red-200 text-3xl mb-4" />
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      "{testimonial.review}"
                    </p>
                    <div className="flex gap-1 mb-3">{renderStars(testimonial.rating)}</div>
                    <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <h4 className="font-semibold text-gray-800">{testimonial.name}</h4>
                        <p className="text-gray-400 text-xs">{testimonial.location}</p>
                        <p className="text-gray-400 text-xs">{testimonial.vehicle}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Carousel Controls */}
          <button 
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -ml-3 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:bg-red-600 hover:text-white transition-all"
          >
            <FaArrowLeft className="text-xs" />
          </button>
          <button 
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 -mr-3 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:bg-red-600 hover:text-white transition-all"
          >
            <FaArrowRight className="text-xs" />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === currentIndex ? 'w-6 bg-red-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 flex flex-wrap justify-center gap-8">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-500 text-sm">100% Genuine Parts</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-500 text-sm">Certified Mechanics</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-500 text-sm">Transparent Pricing</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-500 text-sm">Warranty on Services</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fadeUp 0.5s ease-out forwards;
        }
        .line-clamp-4 {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default Testimonials;